import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { ClassInfoType } from '../../types'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import { InputNew } from '@UI/InputNew'
import cn from 'classnames'
import { Select } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import Button from '@UI/New/Button'
import CreateEditFolderModal from '@pages/Admin/Manage/tabs/ClassesPage/components/CreateEditFolderModal/CreateEditFolderModal'
import { GeneralCourseInformation, RefetchGeneralDataVariantType } from '../..'
import CourseList from '../CourseList/CourseList'
import { classApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { deepEqualObject } from '@/helpers/deepEqualObject'
import ClassSettings, { UnsavedChanges } from '../ClassSettings/ClassSettings'
import { useHistory } from 'react-router-dom'
import { closePreloader, openPreloader } from '@store/preloaderSlice'
import { useDispatch } from 'react-redux'
import { useIsUniversity2035DigitalTraceEnabled } from '@/hooks/useIsUniversity2035DigitalTraceEnabled'
import CatalogSettings from '../CatalogSettings/CatalogSettings'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import { getApiErrorMessage } from '@/helpers/getApiErrorMessage'

interface PageContentProps {
  classInfo: ClassInfoType
  generalData: GeneralCourseInformation
  refetchGeneralData: (variant: RefetchGeneralDataVariantType) => void
  unsavedChanges?: boolean
  setUnsavedChanges?: (value: boolean) => void
  hiddenParts?: {
    main?: boolean
    courses?: boolean
    settings?: boolean
  }
}

export interface PageContentRef {
  handleSaveClass: ({
    onSuccessCallback,
  }: {
    onSuccessCallback?: () => void
  }) => void
  unsavedChangesBeforeSaving: UnsavedChanges
  isSaving: boolean
}

type DeepNullableOptional<T> = {
  [K in keyof T]?: T[K] extends object
    ? DeepNullableOptional<T[K]>
    : T[K] | null
}

type CustomGroupType = ClassInfoType['groups'][number] & {
  key: string
  isPersisted?: boolean
}

const getResponseErrorMessage = (
  response: any,
  fallback = 'Не удалось сохранить изменения'
) => {
  const data = response?.data

  if (typeof data?.error === 'string' && data.error) return data.error
  if (Array.isArray(data?.error) && data.error[0]) return data.error[0]
  if (typeof data?.message === 'string' && data.message) return data.message

  const firstDataError = data?.data && Object.values(data.data)[0]
  if (Array.isArray(firstDataError) && firstDataError[0])
    return firstDataError[0]
  if (typeof firstDataError === 'string' && firstDataError)
    return firstDataError

  return fallback
}

export type ElClassFormDataType = DeepNullableOptional<
  Omit<ClassInfoType, 'groups'> & {
    mass_payment_amount: boolean
    groups: CustomGroupType[]
  }
>

const preparationElClassData = (classInfo: ClassInfoType) => ({
  ...classInfo,
  mass_payment_amount: true,
  change_vks_link_for_everyone:
    classInfo.change_vks_link_for_everyone ?? false,
  groups: classInfo?.groups?.map((group, i) => ({
    ...group,
    key: `${group.id}_${i}`,
    isPersisted: true,
  })),
})

const PageContent = forwardRef<PageContentRef, PageContentProps>(
  (
    {
      classInfo,
      generalData,
      refetchGeneralData,
      unsavedChanges,
      setUnsavedChanges,
      hiddenParts,
    },
    ref
  ) => {
    const addAlert = useAddAlert()
    const history = useHistory()
    const dispatch = useDispatch()
    const isUniversity2035DigitalTraceEnabled =
      useIsUniversity2035DigitalTraceEnabled()
    const [openCreateFolderModal, setOpenCreateFolderModal] = useState(false)
    const [allFolders, setAllFolders] = useState(generalData.allFolders)

    const initElClassFormData = useMemo(
      () => preparationElClassData(classInfo),
      [classInfo]
    )

    const [elClassFormData, setElClassFormData] =
      useState<ElClassFormDataType>(initElClassFormData)

    useEffect(() => {
      setElClassFormData(preparationElClassData(classInfo))
    }, [classInfo])

    console.log('elClassFormData', elClassFormData)

    const [unsavedChangesBeforeSaving, setUnsavedChangesBeforeSaving] =
      useState<UnsavedChanges>([])
    const [saved, setSaved] = useState(false)

    const handlerData = (key: keyof ElClassFormDataType, value: any) => {
      setElClassFormData((prev) => ({ ...prev, [key]: value }))
    }

    const handleSaveClass = ({
      onSuccessCallback,
    }: {
      onSuccessCallback?: () => void
    }) => {
      const normalizedDescription = normalizeTiptapValue(
        elClassFormData?.description ?? ''
      )

      if (
        !!elClassFormData?.access_by_days &&
        !elClassFormData?.custom_payment_period
      ) {
        addAlert({
          type: 'error',
          title: 'Ошибка валидации',
          text: 'Необходимо заполнить количество дней',
        })
        return
      }
      setSaved(true)
      dispatch(openPreloader({ text: 'Идет сохранение' }))
      classApi
        .update(elClassFormData.id, {
          title: elClassFormData.title,
          one_pay: elClassFormData.one_pay,
          mass_add_group: elClassFormData.mass_add_group,
          ignore_payment: elClassFormData.ignore_payment,
          mass_payment_amount: false, // вынесен в отдельный ендпоинт
          amount: elClassFormData.one_pay ? elClassFormData.amount ?? 0 : 0,
          link_vks: elClassFormData.link_vks ?? null,
          change_vks_link_for_everyone:
            elClassFormData.change_vks_link_for_everyone ?? false,
          folder_ids: elClassFormData.folder_ids,
          groups: elClassFormData.groups?.map((group) => ({
            group_id: group?.id,
            tariff_id: group?.tariff_id,
          })),
          orders_group: elClassFormData.orders_group,
          pointer: elClassFormData.pointer,
          auto_access_duration: elClassFormData.auto_access_duration ?? null,
          auto_access_unit: elClassFormData.auto_access_unit ?? 'months',
          tag: elClassFormData?.tag ?? null,
          direct_link_enabled: elClassFormData.direct_link_enabled ?? false,
          access_by_days: elClassFormData?.access_by_days ?? false,
          renew_to_first_day_of_month:
            elClassFormData?.renew_to_first_day_of_month ?? false,
          catalog_to_first_day_of_month:
            elClassFormData?.catalog_to_first_day_of_month ?? false,
          catalog_enabled: elClassFormData?.catalog_enabled ?? false,
          catalog_folder_ids: elClassFormData?.catalog_folder_ids ?? [],
          catalog_sub_folder_ids: elClassFormData?.catalog_sub_folder_ids ?? [],
          description:
            typeof normalizedDescription === 'object'
              ? JSON.stringify(normalizedDescription)
              : normalizedDescription,
          media_icon_id: elClassFormData?.media_icon_id ?? null,
          duration_in_months: elClassFormData?.duration_in_months || null,
          duration_unit: elClassFormData?.duration_unit ?? 'month',
          old_price: elClassFormData?.old_price || null,
          payment_acquiring_id: elClassFormData?.payment_acquiring_id ?? null,
          payment_acquiring_id_secondary:
            elClassFormData?.payment_acquiring_id_secondary ?? null,
          franchise_id: elClassFormData?.franchise_id ?? null,
          disable_promo_code_when_extend:
            elClassFormData?.disable_promo_code_when_extend ?? false,
          u2035_flow_id: elClassFormData?.u2035_flow_id ?? null,
          u2035_module_num: elClassFormData?.u2035_module_num ?? null,
          custom_payment_period: elClassFormData?.custom_payment_period
            ? elClassFormData?.custom_payment_period ?? 0
            : 0,
        })
        .then((resDef) => {
          const res = resDef as any
          if (res.response?.status >= 400) {
            addAlert({
              type: 'error',
              title: 'Ошибка',
              text: getResponseErrorMessage(res.response),
            })
          } else {
            // refetchGeneralData('class')
            addAlert({ title: 'Успешно', text: 'Изменения сохранены' })
            onSuccessCallback
              ? onSuccessCallback()
              : history.push('/manage/classes')
          }
        })
        .catch((err) => {
          addAlert({
            type: 'error',
            title: 'Ошибка',
            text: getApiErrorMessage(err, 'Не удалось сохранить изменения'),
          })
        })
        .finally(() => {
          setSaved(false)
          dispatch(closePreloader())
        })
    }

    useImperativeHandle(ref, () => ({
      handleSaveClass,
      unsavedChangesBeforeSaving,
      isSaving: saved,
    }))

    useEffect(() => {
      setUnsavedChanges?.(
        !deepEqualObject(initElClassFormData, elClassFormData)
      )
    }, [initElClassFormData, elClassFormData])

    return (
      <>
        <div className={classes.mainWrapper}>
          {hiddenParts?.main !== true && (
            <div className={classes.section}>
              <div className={classes.sectionContentGrid2}>
                <div className={classes.fieldWrapper}>
                  <p className={mclasses.label}>
                    Название класса/обр. Траектории
                  </p>
                  {/*@ts-ignore*/}
                  <InputNew
                    className={cn(iclasses.newInputDesign, classes.inputField)}
                    placeholder={'Название правила'}
                    value={elClassFormData.title}
                    onChange={(e: any) =>
                      handlerData('title', e.currentTarget.value)
                    }
                    validate={(elClassFormData?.title ?? '').length < 1}
                    errorText={'Поле не может быть пустым'}
                    typeValid="custom"
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <p className={mclasses.label}>
                    Папки, в которой лежит класс/обр. Траектория
                  </p>
                  {allFolders.length === 0 ? (
                    <Button
                      color="secondary"
                      style={{ width: '100%', height: '100%' }}
                      onClick={() => setOpenCreateFolderModal(true)}
                      leftContent={
                        <PlusOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                    >
                      Создать новую папку
                    </Button>
                  ) : (
                    <Select
                      className={cn(
                        iclasses.newSelect,
                        iclasses.newSelectMultiple,
                        classes.selectField
                      )}
                      popupClassName={iclasses.newSelectPopup}
                      placeholder="Выберите папки"
                      value={elClassFormData?.folder_ids ?? []}
                      onChange={(value) => handlerData('folder_ids', value)}
                      options={allFolders.map((folder) => ({
                        label: folder.title,
                        value: folder.id,
                      }))}
                      mode="multiple"
                    />
                  )}
                </div>
                {isUniversity2035DigitalTraceEnabled && (
                  <>
                    <div className={classes.fieldWrapper}>
                      <p className={mclasses.label}>У2035 flow_id</p>
                      {/*@ts-ignore*/}
                      <InputNew
                        className={cn(
                          iclasses.newInputDesign,
                          classes.inputField,
                        )}
                        placeholder="ID потока У2035"
                        value={elClassFormData.u2035_flow_id ?? ''}
                        onChange={(e: any) =>
                          handlerData('u2035_flow_id', e.currentTarget.value)
                        }
                      />
                    </div>
                    <div className={classes.fieldWrapper}>
                      <p className={mclasses.label}>У2035 module_num</p>
                      {/*@ts-ignore*/}
                      <InputNew
                        className={cn(
                          iclasses.newInputDesign,
                          classes.inputField,
                        )}
                        placeholder="Номер модуля У2035"
                        value={elClassFormData.u2035_module_num ?? ''}
                        onChange={(e: any) =>
                          handlerData('u2035_module_num', e.currentTarget.value)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {hiddenParts?.courses !== true && (
            <div className={classes.section}>
              <div className={classes.sectionTitleWrapper}>
                <p className={classes.sectionTitle}>Список курсов в классе</p>
              </div>
              <p className={classes.sectionDescription}>
                Все курсы, которые вы добавляете в класс или образовательную
                траекторию, автоматически будут доступны учащемуся.
              </p>
              <CourseList
                generalData={generalData}
                elClassFormData={elClassFormData}
                setElClassFormData={setElClassFormData}
                refetchGeneralData={refetchGeneralData}
              />
            </div>
          )}

          {hiddenParts?.settings !== true && (
            <>
              <div className={classes.section}>
                <p className={classes.sectionTitle}>Настройки класса</p>
                <ClassSettings
                  generalData={generalData}
                  elClassFormData={elClassFormData}
                  setElClassFormData={setElClassFormData}
                  refetchGeneralData={refetchGeneralData}
                  //
                  setOtherUnsavedChanges={setUnsavedChangesBeforeSaving}
                />
              </div>
              <div className={classes.section}>
                <p className={classes.sectionTitle}>Настройки каталога</p>
                <CatalogSettings
                  elClassFormData={elClassFormData}
                  setElClassFormData={setElClassFormData}
                />
              </div>
            </>
          )}
        </div>

        {/* Modals */}
        <CreateEditFolderModal
          open={openCreateFolderModal}
          onClose={() => setOpenCreateFolderModal(false)}
          onSuccess={(newFolder) => {
            setAllFolders((prev) => [...prev, newFolder])
            handlerData('folder_ids', [
              ...(elClassFormData?.folder_ids ?? []),
              newFolder.id,
            ])
            setOpenCreateFolderModal(false)
          }}
        />
      </>
    )
  }
)

export default PageContent
